// ProngAnswer — one verification question with its answer choices
// (RRA v2.1 / W3, E2): Yes · No · They couldn't say (ANSWER_STAFF).
//
// Used by the console's oral-verification form and "Record a reply or letter…".
// "They couldn't say" is NEVER a negative and records no evidence for that
// question; the position-to-know question uses the same control with
// `cantSay={false}` (Yes / No only).

import type { ReactNode } from 'react';

import { ANSWER_STAFF } from '@/lib/rraCopy';

export type ProngChoice = 'yes' | 'no' | 'cant_say';

export function ProngAnswer({
  legend,
  name,
  value,
  onChange,
  cantSay = true,
  disabled = false,
  above,
}: {
  /** The question, worded by rraCopy (positionQuestion / disabilityQuestion / needQuestion). */
  legend: string;
  /** Radio-group name; unique per question on the page. */
  name: string;
  value: ProngChoice | '';
  onChange: (next: ProngChoice) => void;
  /** Offer "They couldn't say" (default). False for the position-to-know question. */
  cantSay?: boolean;
  disabled?: boolean;
  /** Shown directly above the question (the spoken definition, the change they asked for). */
  above?: ReactNode;
}) {
  const options: Array<{ value: ProngChoice; label: string }> = [
    { value: 'yes', label: ANSWER_STAFF.yes },
    { value: 'no', label: ANSWER_STAFF.no },
    ...(cantSay ? [{ value: 'cant_say' as const, label: ANSWER_STAFF.cantSay }] : []),
  ];
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }} disabled={disabled}>
      {above}
      <legend className="label">{legend}</legend>
      <div className="cluster" style={{ gap: 'var(--space-5)' }}>
        {options.map((o) => (
          <label
            key={o.value}
            className="cluster"
            style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}
          >
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
